/* istanbul ignore file */
import { fetchTopBannerContent } from '@noths/polaris-client-banners';
import type { FeatureFlags } from '@noths/polaris-client-feature-flags';
import { fetchSiteChromeContent } from '@noths/polaris-client-site-chrome-ui';
import type { Config, StackName } from '@noths/polaris-server-content-fetching';
import { Environment, fetchContentByQuery } from '@noths/polaris-server-content-fetching';
import * as runtimeProcess from 'process';

import { DEVELOPMENT, QA } from 'src/constants/environment';
import type { ContentstackSections } from 'src/types/Contentstack';
import type { PageConfiguration, PageMetadata } from 'src/types/PageProps';
import { logger } from 'src/utils/serverLogger';

/** @deprecated content should be moved to the DX stack */
export const getContentstackEnvironment = (isPreview?: boolean) => {
  if (isPreview) {
    return Environment.Preview;
  }
  const isQa = runtimeProcess.env.ENV_TYPE === QA || process.env.NODE_ENV === DEVELOPMENT;

  return isQa ? Environment.Qa : Environment.Production;
};

export const getContentstackDXEnvironment = (isPreview?: boolean) => {
  return isPreview ? Environment.Preview : Environment.Production;
};

export interface DXPageContentEntry {
  modular_content: ContentstackSections[];
  seo: {
    canonical_url_override: string;
    description: string;
    indexable: boolean;
    title_override: string;
  };
  title: string;
}

interface GlobalContentOptions {
  featureFlags: FeatureFlags;
  isPreview?: boolean;
  urlPath?: string;
}

export const fetchGlobalContent = async ({
  featureFlags,
  isPreview = false,
  urlPath,
}: GlobalContentOptions) => {
  const env = getContentstackEnvironment(isPreview);
  const config = { env, logger };
  const navContentVariantId = featureFlags.nav_content_experiment;

  return Promise.all([
    fetchSiteChromeContent(config, navContentVariantId).catch(() => null),
    fetchTopBannerContent(config, urlPath),
  ]);
};

export const fetchPageContentByQuery = async (
  { contentType }: PageConfiguration,
  { isPreview, pathname }: PageMetadata,
) => {
  const stackName: StackName = 'DX';
  const config: Config = {
    env: getContentstackDXEnvironment(isPreview),
    logger,
    stackName,
  };

  const data = await fetchContentByQuery({
    config,
    contentType,
    query: { url: pathname },
    params: {
      include: [
        'modular_content.department_banner.department_banner_reference',
        'modular_content.department_banner.department_banner_reference.offer_message',
        'modular_content.department_banner.department_banner_reference.offer_message.page_reference',
        'modular_content.membership_banner.membership_banner_content_reference',
        'modular_content.page_carousel.pages.page_reference',
      ],
      only: [
        {
          field: 'url',
          refUid: 'modular_content.page_carousel.pages.page_reference',
        },
        {
          field: 'title',
          refUid: 'modular_content.page_carousel.pages.page_reference',
        },
        {
          field: 'canonical_image.url',
          refUid: 'modular_content.page_carousel.pages.page_reference',
        },
        {
          field: 'url',
          refUid:
            'modular_content.department_banner.department_banner_reference.offer_message.page_reference',
        },
      ],
    },
    timeout: 1500,
  });

  return data.content as DXPageContentEntry;
};
